import { Controller } from "@hotwired/stimulus"
import { startQrScanner, QrScannerHandle } from "../lib/qr_scanner"

/**
 * Shared QR/barcode scanner Stimulus controller.
 *
 * Owns the camera lifecycle (start/stop) and the shared UI states
 * (placeholder, error banner, start/stop buttons) for any surface that
 * needs to scan a QR code or barcode. Dispatches a `qr-scanner:decode`
 * bubbling CustomEvent with `{ token }` on every successful read so
 * consuming controllers stay concerned only with what to do with the
 * scanned value (e.g. populate a form input, POST to a check-in URL,
 * look up a record).
 *
 * Usage:
 *
 *   <div data-controller="qr-scanner"
 *        data-qr-scanner-qrbox-size-value="300"
 *        data-qr-scanner-debounce-ms-value="1500">
 *     <div data-qr-scanner-target="region"></div>
 *     <p data-qr-scanner-target="placeholder">Point the camera at…</p>
 *     <div data-qr-scanner-target="error" class="hidden alert alert-warning">
 *       Camera unavailable.
 *     </div>
 *     <button data-action="qr-scanner#start" data-qr-scanner-target="startButton">Start</button>
 *     <button data-action="qr-scanner#stop" data-qr-scanner-target="stopButton" class="hidden">Stop</button>
 *   </div>
 *
 * Listening for decodes locally (same controller element or descendants):
 *
 *   <div data-controller="event-kiosk"
 *        data-action="qr-scanner:decode->event-kiosk#onScan">
 *     …qr-scanner markup above…
 *   </div>
 *
 * Listening from a controller that sits outside the scanner's DOM tree
 * (e.g. a button elsewhere on the page opening a shared modal):
 *
 *   // In the consumer controller:
 *   static outlets = ["qr-scanner"]
 *
 *   qrScannerOutletConnected(outlet, element) {
 *     element.addEventListener("qr-scanner:decode", this.boundOnScan)
 *   }
 */
export default class QrScannerController extends Controller<HTMLElement> {
  static values = {
    autoStart: { type: Boolean, default: false },
    fps: { type: Number, default: 10 },
    qrboxSize: { type: Number, default: 250 },
    aspectRatio: { type: Number, default: 1.0 },
    debounceMs: { type: Number, default: 0 },
    stopOnDecode: { type: Boolean, default: false },
  }

  static targets = ["region", "placeholder", "error", "startButton", "stopButton"]

  declare readonly autoStartValue: boolean
  declare readonly fpsValue: number
  declare readonly qrboxSizeValue: number
  declare readonly aspectRatioValue: number
  declare readonly debounceMsValue: number
  declare readonly stopOnDecodeValue: boolean
  declare readonly hasRegionTarget: boolean
  declare readonly regionTarget: HTMLElement
  declare readonly hasPlaceholderTarget: boolean
  declare readonly placeholderTarget: HTMLElement
  declare readonly hasErrorTarget: boolean
  declare readonly errorTarget: HTMLElement
  declare readonly hasStartButtonTarget: boolean
  declare readonly startButtonTarget: HTMLButtonElement
  declare readonly hasStopButtonTarget: boolean
  declare readonly stopButtonTarget: HTMLButtonElement

  private scanner: QrScannerHandle | null = null
  private regionElementId = ""
  private lastToken = ""
  private lastDecodedAt = 0

  connect() {
    // html5-qrcode requires an element ID; generate a stable, collision-safe
    // one per mount so multiple scanners can coexist on the same page.
    this.regionElementId = `qr-scanner-region-${Math.random().toString(36).slice(2)}`
    if (this.hasRegionTarget) {
      this.regionTarget.id = this.regionElementId
    }

    if (this.autoStartValue) {
      void this.start()
    }
  }

  disconnect() {
    void this.stop()
  }

  async start() {
    if (this.scanner) return

    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")

    try {
      this.scanner = await startQrScanner({
        elementId: this.regionElementId,
        fps: this.fpsValue,
        qrboxSize: this.qrboxSizeValue,
        aspectRatio: this.aspectRatioValue,
        onDecode: (token: string) => this.onDecode(token),
      })

      if (this.hasStartButtonTarget) this.startButtonTarget.classList.add("hidden")
      if (this.hasStopButtonTarget) this.stopButtonTarget.classList.remove("hidden")
    } catch {
      this.showError()
    }
  }

  async stop() {
    if (this.scanner) {
      await this.scanner.stop()
      this.scanner = null
    }
    if (this.hasStartButtonTarget) this.startButtonTarget.classList.remove("hidden")
    if (this.hasStopButtonTarget) this.stopButtonTarget.classList.add("hidden")
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
  }

  private onDecode(token: string) {
    const now = Date.now()
    if (
      this.debounceMsValue > 0 &&
      token === this.lastToken &&
      now - this.lastDecodedAt < this.debounceMsValue
    ) {
      return
    }
    this.lastToken = token
    this.lastDecodedAt = now

    if (this.stopOnDecodeValue) {
      void this.stop()
    }

    this.dispatch("decode", { detail: { token }, bubbles: true })
  }

  private showError() {
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
    if (this.hasStartButtonTarget) this.startButtonTarget.disabled = true
  }
}
